/**
 * @fileoverview Histórico de versões da DRE
 * @module components/dre/VersionHistory
 */

import { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Stack,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  FileDownload as FileDownloadIcon,
  Compare as CompareIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { BookOpen } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';

import { useVersionStore } from '../../stores/versionStore';
import { useDREStore } from '../../stores/dreStore';
import { useExportDRE } from '../../hooks/useExportDRE';
import { formatDiff } from '../../lib/versionDiff';
import { VersionCompare } from './VersionCompare';
import { CurrentWorkCard } from './CurrentWorkCard';
import { calculateDiff } from '../../lib/versionDiff';
import { ViewPremissasSnapshotModal } from '../modals/ViewPremissasSnapshotModal';
import { RestoreConfirmationModal } from '../modals/RestoreConfirmationModal';
import type { DREVersion } from '../../types/version';

export function VersionHistory() {
  const theme = useTheme();
  const { versions, baseVersionId, isLoading, loadVersions, restoreVersion, deleteVersion, saveVersion } = useVersionStore();
  const { dreData, saveDRE, isSaving } = useDREStore();
  const { exportVersionsToCSV, exportVersionToExcel } = useExportDRE();

  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [versionDescription, setVersionDescription] = useState('');

  // SPRINT 3: Estado para modal de premissas
  const [premissasModalOpen, setPremissasModalOpen] = useState(false);
  const [selectedVersionForPremissas, setSelectedVersionForPremissas] = useState<DREVersion | null>(null);

  // SPRINT 4: Estado para modal de confirmação de restauração (Bug #5)
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<DREVersion | null>(null);

  // DIA 9: Loading states para operações async
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [isRestoringVersion, setIsRestoringVersion] = useState(false);
  const [deletingVersionId, setDeletingVersionId] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // Calcular se há alterações não salvas
  const baseVersion = versions.find(v => v.id === baseVersionId);
  const isDirty = baseVersion && dreData
    ? calculateDiff(baseVersion.dreData, dreData).length > 0 
    : false;
  const changeCount = baseVersion && dreData
    ? calculateDiff(baseVersion.dreData, dreData).length 
    : 0;

  const handleSave = async () => {
    await saveDRE();
  };

  const handleSaveVersion = () => {
    setSaveDialogOpen(true);
  };

  const handleConfirmSaveVersion = async () => {
    if (!versionName.trim()) {
      toast.error('Nome da versão é obrigatório');
      return;
    }
    
    setIsSavingVersion(true);
    try {
      await saveVersion(versionName, versionDescription || undefined);
      setSaveDialogOpen(false);
      setVersionName('');
      setVersionDescription('');
      toast.success('Versão salva com sucesso!');
    } catch (error) {
      console.error('[VersionHistory] Erro ao salvar versão:', error);
      toast.error('Erro ao salvar versão. Tente novamente.');
    } finally {
      setIsSavingVersion(false);
    }
  };

  const handleCancelSaveVersion = () => {
    setSaveDialogOpen(false);
    setVersionName('');
    setVersionDescription('');
  };

  const handleSelectVersion = (versionId: string) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionId)) {
        return prev.filter((id) => id !== versionId);
      }
      if (prev.length >= 2) {
        return [prev[1], versionId]; // Mantém apenas as 2 últimas seleções
      }
      return [...prev, versionId];
    });
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      setCompareDialogOpen(true);
    }
  };

  const handleCloseCompare = () => {
    setCompareDialogOpen(false);
    setCompareMode(false);
    setSelectedVersions([]);
  };

  /**
   * SPRINT 4 - Bug #5: Abre modal de confirmação para restaurar versão
   * Em vez de restaurar diretamente, mostra preview das mudanças
   */
  const handleRestoreClick = (version: DREVersion) => {
    console.log('[VersionHistory] 🔍 handleRestoreClick chamado', { 
      versionId: version.id, 
      versionName: version.name,
      hasDreData: !!dreData 
    });

    if (!dreData) {
      console.error('[VersionHistory] ❌ dreData não disponível');
      toast.error('Nenhuma DRE carregada');
      return;
    }

    // Calcular mudanças entre versão atual e versão a restaurar
    const edits = calculateDiff(dreData, version.dreData);

    console.log('[VersionHistory] Diff calculado', { 
      editsCount: edits.length,
      firstEdit: edits[0]
    });

    if (edits.length === 0) {
      console.warn('[VersionHistory] ℹ️ Nenhuma mudança detectada');
      toast('Esta versão é idêntica à atual', {
        icon: 'ℹ️',
      });
      return;
    }

    console.log('[VersionHistory] ✅ Abrindo modal de confirmação');
    
    // Abrir modal de confirmação
    setVersionToRestore(version);
    setRestoreModalOpen(true);
  };

  /**
   * SPRINT 4 - Bug #5: Confirma restauração com checkpoint automático
   */
  const handleConfirmRestore = async () => {
    if (!versionToRestore || !dreData) return;

    setIsRestoringVersion(true);
    try {
      const edits = calculateDiff(dreData, versionToRestore.dreData);

      // 1. Criar checkpoint automático ANTES de restaurar
      // 🐛 FIX: Evitar duplicação "Checkpoint antes de restaurar Checkpoint..."
      let versionName: string;
      if (versionToRestore.name.startsWith('Checkpoint antes de restaurar')) {
        // Se já é um checkpoint, extrair apenas a parte final após o prefixo
        versionName = versionToRestore.name.replace('Checkpoint antes de restaurar ', '');
      } else {
        // Caso contrário, usar número da versão
        versionName = `v${versionToRestore.versionNumber}`;
      }
      
      const checkpointName = `Checkpoint antes de restaurar ${versionName}`;
      const checkpointDescription = `Snapshot automático criado antes de restaurar versão ${versionToRestore.versionNumber}. ${edits.length} mudança(s) salva(s) neste checkpoint.`;

      await saveVersion(checkpointName, checkpointDescription, true); // true = is_auto_checkpoint

      // 2. Executar restauração
      await restoreVersion(versionToRestore.id);

      // 3. Fechar modal
      setRestoreModalOpen(false);
      setVersionToRestore(null);

      toast.success('Checkpoint salvo no histórico', {
        duration: 4000,
      });
    } catch (error) {
      console.error('[VersionHistory] Erro ao restaurar versão:', error);
      toast.error('Erro ao criar checkpoint. Restauração cancelada.');
    } finally {
      setIsRestoringVersion(false);
    }
  };

  /**
   * Converte diff para formato do modal de confirmação
   */
  const getEditsForModal = () => {
    if (!versionToRestore || !dreData) return [];

    const diffs = calculateDiff(dreData, versionToRestore.dreData);

    return diffs.map((diff) => ({
      company: diff.company as 'Sétima' | '555 Studios',
      year: diff.year,
      field: diff.field,
      oldValue: diff.oldValue / 1_000_000, // Converter para milhões
      newValue: diff.newValue / 1_000_000,
      percentChange: diff.oldValue !== 0 
        ? ((diff.newValue - diff.oldValue) / diff.oldValue) * 100 
        : 100,
    }));
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (versions.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nenhuma versão salva ainda.
          <br />
          Clique em "Salvar Versão" para criar a primeira.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Stack spacing={3}>
        {/* Seção de Trabalho Atual */}
        <CurrentWorkCard
          baseVersion={baseVersion}
          isDirty={isDirty}
          changeCount={changeCount}
          onSave={handleSave}
          onSaveVersion={handleSaveVersion}
          isSaving={isSaving}
        />

        {/* Histórico de Versões */}
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <BookOpen size={18} />
              <Typography variant="subtitle1" fontWeight="bold">
                Histórico de Versões ({versions.length})
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              {compareMode ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setCompareMode(false);
                      setSelectedVersions([]);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    startIcon={<CompareIcon />}
                    variant="contained"
                    onClick={handleCompare}
                    disabled={selectedVersions.length !== 2}
                  >
                    Comparar Selecionadas ({selectedVersions.length}/2)
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    startIcon={<CompareIcon />}
                    variant="outlined"
                    onClick={() => setCompareMode(true)}
                    disabled={versions.length < 2}
                  >
                    Comparar Versões
                  </Button>
                  <Button
                    startIcon={<FileDownloadIcon />}
                    variant="outlined"
                    onClick={exportVersionsToCSV}
                  >
                    Exportar Histórico (CSV)
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Paper>

      {versions.map((version, index) => {
        const isActive = version.id === baseVersionId;
        const shouldShowActions = index > 0;
        
        console.log('[VersionHistory] Renderizando versão', {
          index,
          versionId: version.id,
          versionName: version.name,
          isActive,
          shouldShowActions,
          baseVersionId,
        });
        
        return (
        <Card 
          key={version.id} 
          variant="outlined"
          sx={{
            border: selectedVersions.includes(version.id) ? 2 : isActive ? 3 : 1,
            borderColor: selectedVersions.includes(version.id) 
              ? 'primary.main' 
              : isActive 
                ? 'success.main' 
                : 'divider',
            bgcolor: isActive ? 'success.50' : 'background.paper',
            boxShadow: isActive ? 2 : 0,
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="start" mb={1}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {compareMode && (
                    <Checkbox
                      checked={selectedVersions.includes(version.id)}
                      onChange={() => handleSelectVersion(version.id)}
                      disabled={!selectedVersions.includes(version.id) && selectedVersions.length >= 2}
                    />
                  )}
                  <Typography variant="h6">
                    {version.name}
                  </Typography>
                  {isActive && (
                    <Chip 
                      label="ATIVA" 
                      color="success" 
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                  
                  {/* SPRINT 3: Badge mostrando se premissas foram salvas */}
                  {version.premissasSnapshot && (
                    <Chip 
                      icon={<AssessmentIcon fontSize="small" />}
                      label="Premissas Salvas" 
                      color="primary" 
                      size="small"
                      variant="outlined"
                      sx={{ 
                        fontWeight: 'medium',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'primary.50',
                          borderColor: 'primary.main',
                        }
                      }}
                      onClick={() => {
                        setSelectedVersionForPremissas(version);
                        setPremissasModalOpen(true);
                      }}
                    />
                  )}
                </Stack>
                {version.description && (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {version.description}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} mb={2}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(version.createdAt, {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {version.createdBy}
                </Typography>
              </Stack>
            </Stack>

            {version.diff && version.diff.length > 0 && (
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 1.5, 
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.03)' 
                    : 'rgba(0, 0, 0, 0.02)' 
                }}
              >
                <Typography 
                  variant="caption" 
                  fontWeight="bold" 
                  sx={{ 
                    color: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.7)' 
                      : 'rgba(0, 0, 0, 0.6)',
                    mb: 1,
                    display: 'block'
                  }}
                >
                  {version.diff.length} alteraç{version.diff.length === 1 ? 'ão' : 'ões'}:
                </Typography>
                <Stack spacing={0.5}>
                  {version.diff.slice(0, 3).map((diff, i) => (
                    <Typography 
                      key={i} 
                      variant="caption" 
                      sx={{ 
                        fontFamily: 'monospace',
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.85)' 
                          : 'rgba(0, 0, 0, 0.87)'
                      }}
                    >
                      {formatDiff(diff)}
                    </Typography>
                  ))}
                  {version.diff.length > 3 && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.6)' 
                          : 'rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      +{version.diff.length - 3} mais...
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}
          </CardContent>

          {index > 0 && (
            <CardActions>
              <Button
                size="small"
                startIcon={<RestoreIcon />}
                onClick={() => handleRestoreClick(version)}
                disabled={isRestoringVersion}
              >
                Restaurar
              </Button>
              <Button
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={() => exportVersionToExcel(version.id)}
              >
                Exportar
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={deletingVersionId === version.id ? undefined : <DeleteIcon />}
                onClick={async () => {
                  setDeletingVersionId(version.id);
                  try {
                    await deleteVersion(version.id);
                    toast.success('Versão excluída');
                  } catch (error) {
                    console.error('[VersionHistory] Erro ao excluir versão:', error);
                    toast.error('Erro ao excluir versão');
                  } finally {
                    setDeletingVersionId(null);
                  }
                }}
                disabled={deletingVersionId === version.id}
              >
                {deletingVersionId === version.id ? 'Excluindo...' : 'Excluir'}
              </Button>
            </CardActions>
          )}
        </Card>
      )})}
    </Stack>

    {/* Dialog de comparação */}
    {compareDialogOpen && selectedVersions.length === 2 && (
      <VersionCompare
        open={compareDialogOpen}
        onClose={handleCloseCompare}
        version1Id={selectedVersions[0]}
        version2Id={selectedVersions[1]}
      />
    )}

    {/* Dialog para salvar nova versão */}
    <Dialog open={saveDialogOpen} onClose={handleCancelSaveVersion} maxWidth="sm" fullWidth>
      <DialogTitle>Salvar Nova Versão</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            label="Nome da Versão *"
            fullWidth
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            placeholder="Ex: Projeção Q1 2024"
            helperText="Nome para identificar esta versão"
          />
          <TextField
            label="Descrição (opcional)"
            fullWidth
            multiline
            rows={3}
            value={versionDescription}
            onChange={(e) => setVersionDescription(e.target.value)}
            placeholder="Ex: Ajuste nas projeções após reunião com diretoria"
            helperText="Detalhes sobre as mudanças desta versão"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelSaveVersion} disabled={isSavingVersion}>
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirmSaveVersion} 
          variant="contained"
          disabled={!versionName.trim() || isSavingVersion}
          startIcon={isSavingVersion ? <CircularProgress size={16} /> : undefined}
        >
          {isSavingVersion ? 'Salvando...' : 'Salvar Versão'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* SPRINT 3: Modal de Premissas Snapshot */}
    {selectedVersionForPremissas && selectedVersionForPremissas.premissasSnapshot && (
      <ViewPremissasSnapshotModal
        open={premissasModalOpen}
        onClose={() => {
          setPremissasModalOpen(false);
          setSelectedVersionForPremissas(null);
        }}
        snapshot={selectedVersionForPremissas.premissasSnapshot}
        versionName={selectedVersionForPremissas.name}
      />
    )}

    {/* SPRINT 4 - Bug #5: Modal de confirmação para restaurar versão */}
    {versionToRestore && (
      <RestoreConfirmationModal
        open={restoreModalOpen}
        onClose={() => {
          setRestoreModalOpen(false);
          setVersionToRestore(null);
        }}
        onConfirm={handleConfirmRestore}
        edits={getEditsForModal()}
        versionName={versionToRestore.name}
        isRestoring={isRestoringVersion}
      />
    )}
    </>
  );
}
